import { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Droplet, TrendingUp, Activity } from 'lucide-react';
import { toast } from 'sonner';

const WaterMeters = ({ user }) => {
  const [readings, setReadings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    generalMeterCurrent: 0,
    generalMeterPrevious: 0,
    poolMeterCurrent: 0,
    poolMeterPrevious: 0,
    notes: ''
  });

  const monthNames = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [readingsRes, statsRes] = await Promise.all([
        axiosInstance.get('/water-meters'),
        axiosInstance.get('/water-meters/stats')
      ]);
      setReadings(readingsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post('/water-meters', formData);
      toast.success('Lettura registrata!');
      fetchData();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    }
  };

  const resetForm = () => {
    setFormData({
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      generalMeterCurrent: 0,
      generalMeterPrevious: 0,
      poolMeterCurrent: 0,
      poolMeterPrevious: 0,
      notes: ''
    });
  };

  const calculateConsumptions = () => {
    const generalConsumption = formData.generalMeterCurrent - formData.generalMeterPrevious;
    const poolConsumption = formData.poolMeterCurrent - formData.poolMeterPrevious;
    const sanitaryConsumption = generalConsumption - poolConsumption;
    return { generalConsumption, poolConsumption, sanitaryConsumption };
  };

  const canEdit = user?.role === 'admin' || user?.role === 'maintainer';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="water-meters-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Contatori Acqua</h1>
          <p className="text-gray-600">Monitoraggio consumo acqua generale e piscine</p>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={resetForm}
                className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                data-testid="add-water-meter-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuova Lettura
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="water-meter-dialog">
              <DialogHeader>
                <DialogTitle>Nuova Lettura Contatori</DialogTitle>
                <DialogDescription>
                  Inserisci le letture dei contatori acqua
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="month">Mese</Label>
                    <select
                      id="month"
                      value={formData.month}
                      onChange={(e) => setFormData({ ...formData, month: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      data-testid="water-meter-month-select"
                    >
                      {monthNames.map((name, index) => (
                        <option key={index + 1} value={index + 1}>{name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="year">Anno</Label>
                    <Input
                      id="year"
                      type="number"
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                      required
                      data-testid="water-meter-year-input"
                    />
                  </div>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg space-y-3">
                  <h4 className="font-medium text-gray-900">Contatore Generale</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="generalMeterCurrent">Lettura Corrente (m³)</Label>
                      <Input
                        id="generalMeterCurrent"
                        type="number"
                        step="0.01"
                        value={formData.generalMeterCurrent}
                        onChange={(e) => setFormData({ ...formData, generalMeterCurrent: parseFloat(e.target.value) || 0 })}
                        required
                        data-testid="general-meter-current-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="generalMeterPrevious">Lettura Precedente (m³)</Label>
                      <Input
                        id="generalMeterPrevious"
                        type="number"
                        step="0.01"
                        value={formData.generalMeterPrevious}
                        onChange={(e) => setFormData({ ...formData, generalMeterPrevious: parseFloat(e.target.value) || 0 })}
                        required
                        data-testid="general-meter-previous-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-cyan-50 p-4 rounded-lg space-y-3">
                  <h4 className="font-medium text-gray-900">Contatore Piscine</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="poolMeterCurrent">Lettura Corrente (m³)</Label>
                      <Input
                        id="poolMeterCurrent"
                        type="number"
                        step="0.01"
                        value={formData.poolMeterCurrent}
                        onChange={(e) => setFormData({ ...formData, poolMeterCurrent: parseFloat(e.target.value) || 0 })}
                        required
                        data-testid="pool-meter-current-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="poolMeterPrevious">Lettura Precedente (m³)</Label>
                      <Input
                        id="poolMeterPrevious"
                        type="number"
                        step="0.01"
                        value={formData.poolMeterPrevious}
                        onChange={(e) => setFormData({ ...formData, poolMeterPrevious: parseFloat(e.target.value) || 0 })}
                        required
                        data-testid="pool-meter-previous-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 p-4 rounded-lg space-y-2">
                  <h4 className="font-medium text-gray-900 mb-3">Consumi Calcolati</h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Consumo Generale</p>
                      <p className="text-xl font-bold text-blue-600">
                        {calculateConsumptions().generalConsumption.toFixed(2)} m³
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Consumo Piscine</p>
                      <p className="text-xl font-bold text-cyan-600">
                        {calculateConsumptions().poolConsumption.toFixed(2)} m³
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Consumo Sanitario</p>
                      <p className="text-xl font-bold text-green-600">
                        {calculateConsumptions().sanitaryConsumption.toFixed(2)} m³
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="notes">Note</Label>
                  <textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
                    data-testid="water-meter-notes-textarea"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                  data-testid="water-meter-submit-button"
                >
                  Registra Lettura
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Stats Cards */}
      {stats && stats.latestReading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Card className="shadow-lg border-0">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
                <Droplet className="w-4 h-4 mr-2" />
                Consumo Generale
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-blue-600">{stats.latestReading.generalConsumption.toFixed(2)} m³</p>
              <p className="text-xs text-gray-500 mt-1">Ultimo mese</p>
            </CardContent>
          </Card>

          <Card className="shadow-lg border-0">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
                <Activity className="w-4 h-4 mr-2" />
                Consumo Piscine
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-cyan-600">{stats.latestReading.poolConsumption.toFixed(2)} m³</p>
              <p className="text-xs text-gray-500 mt-1">Ultimo mese</p>
            </CardContent>
          </Card>

          <Card className="shadow-lg border-0">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
                <TrendingUp className="w-4 h-4 mr-2" />
                Consumo Sanitario
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-green-600">{stats.latestReading.sanitaryConsumption.toFixed(2)} m³</p>
              <p className="text-xs text-gray-500 mt-1">Ultimo mese</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Readings History */}
      <Card className="shadow-lg border-0">
        <CardHeader>
          <CardTitle>Storico Letture</CardTitle>
          <CardDescription>Cronologia delle letture contatori</CardDescription>
        </CardHeader>
        <CardContent>
          {readings.length === 0 ? (
            <p className="text-gray-500 text-center py-8" data-testid="no-readings">Nessuna lettura disponibile</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="readings-table">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Periodo</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-700">Consumo Generale (m³)</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-700">Consumo Piscine (m³)</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-700">Consumo Sanitario (m³)</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Registrato da</th>
                  </tr>
                </thead>
                <tbody>
                  {readings.map((reading) => (
                    <tr key={reading.id} className="border-b border-gray-100 hover:bg-blue-50 transition-colors" data-testid={`reading-row-${reading.id}`}>
                      <td className="py-3 px-4 font-medium">{monthNames[reading.month - 1]} {reading.year}</td>
                      <td className="py-3 px-4 text-right font-medium text-blue-600">{reading.generalConsumption.toFixed(2)}</td>
                      <td className="py-3 px-4 text-right font-medium text-cyan-600">{reading.poolConsumption.toFixed(2)}</td>
                      <td className="py-3 px-4 text-right font-bold text-green-600">{reading.sanitaryConsumption.toFixed(2)}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{reading.recordedBy}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WaterMeters;